import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { RunAllButton } from "./ExecutionListCard";

/**
 * "Run all" das abas SSCUI e App Fiori da Execução, com a confirmação e o "Stop queue".
 * A fila é da tela (ruling E-8): o motor de conversas não tem fila, então quem roda uma
 * de cada vez é o `ActivityExecution`; aqui só o botão, o diálogo e o parar.
 */
export function ConfigRunAllControls({
  count,
  skipped = 0,
  disabledReason,
  queueRunning,
  onConfirm,
  onPrepare,
  onStop,
}: {
  /** Configurações que entram na fila: aprovadas, ainda não concluídas, extraídas e, na SSCUI, com link para o SAP. */
  count: number;
  /** Aprovadas sem extração, que a fila pula. */
  skipped?: number;
  /** Por que o "Run all" não pode começar, já traduzido; `null` libera. */
  disabledReason: string | null;
  queueRunning: boolean;
  onConfirm: () => void;
  onPrepare?: () => Promise<void>;
  onStop: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const prepare = async () => {
    setLoading(true);
    setFailed(false);
    try {
      await onPrepare?.();
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };
  const blocked = queueRunning || disabledReason !== null;
  return (
    <>
      <RunAllButton
        label={t("Run all configurations")}
        // Com a fila andando o botão trava sem motivo à vista: o "Stop queue" ao lado já diz.
        disabledReason={blocked ? (disabledReason ?? "") : null}
        onClick={() => {
          setOpen(true);
          void prepare();
        }}
      />
      {queueRunning && (
        <Button variant="outline" className="h-9" onClick={onStop}>
          {t("Stop queue")}
        </Button>
      )}
      <Dialog open={open} onOpenChange={(next) => !next && setOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("Run all {{count}} configurations?", { count })}
            </DialogTitle>
            {loading && <p role="status">{t("Loading…")}</p>}
            {!loading && !failed && count === 0 && (
              <p role="status">{t("Nothing to run")}</p>
            )}
            {failed && (
              <p role="alert">
                <button onClick={() => void prepare()}>{t("Retry")}</button>
              </p>
            )}
            <DialogDescription>
              {t("They run one at a time, each in its own conversation.")}
            </DialogDescription>
            {skipped > 0 && (
              <p className="text-sm font-medium text-[#8a5200]">
                {t("{{count}} not extracted configurations will be skipped.", {
                  count: skipped,
                })}
              </p>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("No")}
            </Button>
            <Button
              disabled={loading || failed || count === 0}
              onClick={() => {
                setOpen(false);
                onConfirm();
              }}
            >
              {t("Yes, run all")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
